import React, { useEffect, useState, useCallback } from "react";
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Avatar,
  Chip,
  Fade,
  Zoom,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import axios from "axios";
import { backend_url } from "../App";
import {
  CellTower as CellTowerIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Science as ScienceIcon,
  Dashboard as DashboardIcon,
  Close as CloseIcon,
} from "@mui/icons-material";

export default function WeatherStations() {
  const [towers, setTowers] = useState([]);
  const [sensors, setSensors] = useState([]);
  const [filteredSensors, setFilteredSensors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterType, setFilterType] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTower, setSelectedTower] = useState("all");
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [stats, setStats] = useState({
    totalTowers: 0,
    totalSensors: 0,
  });
  const [dashboardOpen, setDashboardOpen] = useState(false);

  // Dashboard link
  const dashboardLink =
    "https://icarus.lums.edu.pk/dashboard/c3e6ece0-539c-11f0-af37-719941c4ace8?publicId=05acac90-5beb-11f0-af37-719941c4ace8&state=W3siaWQiOiJkZWZhdWx0IiwicGFyYW1zIjp7ImVudGl0eUlkIjp7ImlkIjoiNDk1Y2RhZTEtZWY3Yy0xMWVmLWFmMzctNzE5OTQxYzRhY2U4IiwiZW50aXR5VHlwZSI6IkRFVklDRSJ9LCJlbnRpdHlOYW1lIjoiMjUwMi1MTEctQVd9fV0%3D";

  // 3D effect styles
  const card3D = {
    background: "linear-gradient(145deg, #ffffff 0%, #f5f5f5 100%)",
    borderRadius: "20px",
    boxShadow: "20px 20px 60px #d9d9d9, -20px -20px 60px #ffffff",
    transition: "transform 0.3s ease, box-shadow 0.3s ease",
    "&:hover": {
      transform: "translateY(-5px)",
      boxShadow: "25px 25px 75px #d9d9d9, -25px -25px 75px #ffffff",
    },
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    console.log("🔄 Fetching weather station data...");

    try {
      // Fetch towers
      console.log(
        "📡 Fetching towers from:",
        `${backend_url}/core/api/towers/`,
      );
      const towersResponse = await axios.get(
        `${backend_url}/core/api/towers/`,
        {
          headers: {
            Authorization: `Token ${localStorage.getItem("token")}`,
          },
        },
      );

      console.log("✅ Towers response:", towersResponse.data);
      const towersData =
        towersResponse.data.results || towersResponse.data || [];
      setTowers(towersData);

      // Extract all sensors from towers
      let allSensors = [];
      towersData.forEach((tower) => {
        if (tower.sensors && tower.sensors.length > 0) {
          const towerSensors = tower.sensors.map((sensor) => ({
            ...sensor,
            tower_id: tower.id,
            tower_name: tower.name,
            tower_display: `T${tower.id} - ${tower.name}`,
            tower_lat: tower.lat,
            tower_lng: tower.lng,
          }));
          allSensors = [...allSensors, ...towerSensors];
        }
      });

      console.log("📊 Extracted sensors from towers:", allSensors);
      setSensors(allSensors);
      setFilteredSensors(allSensors);

      setStats({
        totalTowers: towersData.length,
        totalSensors: allSensors.length,
      });

      setLastUpdated(new Date());
    } catch (err) {
      console.error("❌ Error fetching weather station data:", err);
      console.error("Error details:", {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
      });
      setError(
        err.response?.data?.detail ||
          err.message ||
          "Failed to load weather station data",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();

    // Refresh data every 5 minutes
    const interval = setInterval(fetchData, 300000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Apply filters when dependencies change
  useEffect(() => {
    if (sensors.length > 0) {
      console.log("🔍 Applying filters:", {
        filterType,
        selectedTower,
        searchTerm,
      });

      let filtered = [...sensors];

      // Filter by sensor type
      if (filterType !== "all") {
        filtered = filtered.filter(
          (s) => s.sensor_type?.toLowerCase() === filterType.toLowerCase(),
        );
      }

      // Filter by tower
      if (selectedTower !== "all") {
        filtered = filtered.filter(
          (s) => s.tower_id === parseInt(selectedTower),
        );
      }

      // Filter by search term
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        filtered = filtered.filter(
          (s) =>
            s.name?.toLowerCase().includes(term) ||
            (s.tower_name && s.tower_name.toLowerCase().includes(term)) ||
            s.sensor_type?.toLowerCase().includes(term) ||
            s.id?.toString().includes(term),
        );
      }

      console.log("✅ Filtered sensors:", filtered.length);
      setFilteredSensors(filtered);
      setPage(0);
    }
  }, [sensors, filterType, selectedTower, searchTerm]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRefresh = () => {
    fetchData();
  };

  const handleOpenDashboard = () => {
    setDashboardOpen(true);
  };

  const handleCloseDashboard = () => {
    setDashboardOpen(false);
  };

  const handleExport = () => {
    console.log("📥 Exporting data...");

    const csvContent = [
      ["ID", "Name", "Type", "Tower", "Latitude", "Longitude"],
      ...filteredSensors.map((s) => [
        s.id,
        s.name || `${s.sensor_type} Sensor`,
        s.sensor_type || "Unknown",
        s.tower_display ||
          `T${s.tower_id} - ${s.tower_name}` ||
          `Tower ${s.tower_id}`,
        s.lat?.toFixed(4) || "N/A",
        s.lng?.toFixed(4) || "N/A",
      ]),
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `weather-stations-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();

    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, textAlign: "center", py: 8 }}>
        <Zoom in={true}>
          <Box>
            <CircularProgress size={80} sx={{ color: "#ff4444", mb: 3 }} />
            <Typography variant="h5" sx={{ color: "#666", mb: 2 }}>
              Loading Weather Stations
            </Typography>
            <LinearProgress
              sx={{
                maxWidth: 400,
                mx: "auto",
                borderRadius: 10,
                "& .MuiLinearProgress-bar": {
                  background: "linear-gradient(90deg, #ff8c00, #ff4444)",
                },
              }}
            />
            <Typography variant="body2" sx={{ color: "#999", mt: 2 }}>
              Fetching tower and sensor data...
            </Typography>
          </Box>
        </Zoom>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Fade in={true}>
          <Alert
            severity="error"
            sx={{
              borderRadius: "16px",
              boxShadow: "0 4px 20px rgba(244,67,54,0.2)",
            }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={handleRefresh}
                startIcon={<RefreshIcon />}
              >
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        </Fade>
      </Container>
    );
  }

  return (
    <Fade in={true}>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {/* Header with 3D effect */}
        <Paper
          sx={{
            p: 3,
            mb: 4,
            ...card3D,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Box>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  mb: 1,
                  textShadow: "2px 2px 4px rgba(0,0,0,0.3)",
                }}
              >
                Weather Stations
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Monitoring {stats.totalTowers} towers with {stats.totalSensors}{" "}
                sensors
              </Typography>
              <Typography
                variant="caption"
                sx={{ opacity: 0.7, display: "block", mt: 1 }}
              >
                Last updated: {lastUpdated.toLocaleString()}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<DashboardIcon />}
                onClick={handleOpenDashboard}
                sx={{
                  bgcolor: "#ff4444",
                  color: "white",
                  borderRadius: "30px",
                  px: 3,
                  "&:hover": {
                    bgcolor: "#ff6666",
                  },
                }}
              >
                View Dashboard
              </Button>
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  backdropFilter: "blur(10px)",
                  color: "white",
                  borderRadius: "30px",
                  px: 3,
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.3)",
                  },
                }}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={handleExport}
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  backdropFilter: "blur(10px)",
                  color: "white",
                  borderRadius: "30px",
                  px: 3,
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.3)",
                  },
                }}
              >
                Export
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Stats Cards with 3D effects */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[
            {
              icon: <CellTowerIcon sx={{ fontSize: 40 }} />,
              value: stats.totalTowers,
              label: "Total Towers",
              gradient: "linear-gradient(135deg, #2196f3 0%, #1976d2 100%)",
            },
            {
              icon: <ScienceIcon sx={{ fontSize: 40 }} />,
              value: stats.totalSensors,
              label: "Total Sensors",
              gradient: "linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)",
            },
          ].map((stat, index) => (
            <Grid item xs={12} sm={6} md={6} key={index}>
              <Zoom in={true} style={{ transitionDelay: `${index * 100}ms` }}>
                <Paper
                  sx={{
                    p: 3,
                    background: stat.gradient,
                    borderRadius: "20px",
                    color: "white",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-10px) scale(1.05)",
                      boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        bgcolor: "rgba(255,255,255,0.2)",
                        borderRadius: "50%",
                        p: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {stat.icon}
                    </Box>
                  </Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {stat.label}
                  </Typography>
                </Paper>
              </Zoom>
            </Grid>
          ))}
        </Grid>

        {/* Filters with 3D effect */}
        <Paper
          sx={{
            p: 3,
            mb: 3,
            ...card3D,
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Sensor Type</InputLabel>
                <Select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  label="Sensor Type"
                  sx={{ borderRadius: "25px" }}
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="temperature">Temperature</MenuItem>
                  <MenuItem value="humidity">Humidity</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Tower</InputLabel>
                <Select
                  value={selectedTower}
                  onChange={(e) => setSelectedTower(e.target.value)}
                  label="Tower"
                  sx={{ borderRadius: "25px" }}
                >
                  <MenuItem value="all">All Towers</MenuItem>
                  {towers.map((tower) => (
                    <MenuItem key={tower.id} value={tower.id}>
                      T{tower.id} - {tower.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Search Sensors"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "25px",
                    height: "50px", // match other fields
                  },
                  "& .MuiOutlinedInput-input": {
                    padding: "10px 14px", // keeps text vertically centered
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={2}>
              <Paper
                sx={{
                  p: 1,
                  bgcolor: "#f5f5f5",
                  borderRadius: "25px",
                  textAlign: "center",
                }}
              >
                <Typography variant="body2" sx={{ color: "#666" }}>
                  {filteredSensors.length} sensor
                  {filteredSensors.length !== 1 ? "s" : ""}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Paper>

        {/* Sensors Table with 3D effect */}
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: "20px",
            overflow: "hidden",
            ...card3D,
          }}
        >
          <Table>
            <TableHead
              sx={{
                bgcolor: "#f5f5f5",
                borderBottom: "2px solid #ff4444",
              }}
            >
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>ID</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Sensor Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Tower</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Latitude</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Longitude</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSensors
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((sensor, index) => (
                  <Fade
                    in={true}
                    key={sensor.id}
                    style={{ transitionDelay: `${index * 30}ms` }}
                  >
                    <TableRow
                      sx={{
                        "&:hover": {
                          bgcolor: "#f8f9fa",
                          transform: "scale(1.01)",
                          transition: "all 0.2s ease",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        },
                      }}
                    >
                      <TableCell>
                        <Chip
                          label={`#${sensor.id}`}
                          size="small"
                          sx={{
                            bgcolor: "#f0f0f0",
                            fontWeight: 600,
                            fontFamily: "monospace",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {sensor.name || `${sensor.sensor_type} Sensor`}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={sensor.sensor_type || "Unknown"}
                          size="small"
                          sx={{
                            bgcolor:
                              sensor.sensor_type === "Temperature"
                                ? "#ff9800"
                                : "#2196f3",
                            color: "white",
                            fontWeight: 500,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <CellTowerIcon sx={{ fontSize: 16, color: "#666" }} />
                          <Typography variant="body2">
                            {sensor.tower_display ||
                              `T${sensor.tower_id} - ${sensor.tower_name}`}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {sensor.lat ? (
                          <Typography variant="body2">
                            {sensor.lat.toFixed(4)}
                          </Typography>
                        ) : (
                          <Typography variant="caption" sx={{ color: "#999" }}>
                            N/A
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {sensor.lng ? (
                          <Typography variant="body2">
                            {sensor.lng.toFixed(4)}
                          </Typography>
                        ) : (
                          <Typography variant="caption" sx={{ color: "#999" }}>
                            N/A
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  </Fade>
                ))}

              {filteredSensors.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <Zoom in={true}>
                      <Box>
                        <ScienceIcon
                          sx={{ fontSize: 60, color: "#ccc", mb: 2 }}
                        />
                        <Typography variant="h6" sx={{ color: "#666", mb: 1 }}>
                          No sensors found
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#999" }}>
                          Try adjusting your filters or refresh the data
                        </Typography>
                        <Button
                          variant="outlined"
                          startIcon={<RefreshIcon />}
                          onClick={handleRefresh}
                          sx={{ mt: 2, borderRadius: "25px" }}
                        >
                          Refresh
                        </Button>
                      </Box>
                    </Zoom>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={filteredSensors.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: "1px solid #f0f0f0",
              ".MuiTablePagination-select": {
                borderRadius: "15px",
              },
            }}
          />
        </TableContainer>

        {/* Tower Cards with 3D effects */}
        {towers.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Typography
              variant="h5"
              sx={{ mb: 3, fontWeight: 600, color: "#333" }}
            >
              Tower Locations
            </Typography>
            <Grid container spacing={3}>
              {towers.map((tower, index) => {
                const towerSensors = sensors.filter(
                  (s) => s.tower_id === tower.id,
                );

                return (
                  <Grid item xs={12} sm={6} md={4} key={tower.id}>
                    <Zoom
                      in={true}
                      style={{ transitionDelay: `${index * 100}ms` }}
                    >
                      <Paper
                        sx={{
                          p: 3,
                          background:
                            "linear-gradient(145deg, #ffffff, #f5f5f5)",
                          borderRadius: "20px",
                          boxShadow:
                            "10px 10px 30px #d9d9d9, -10px -10px 30px #ffffff",
                          transition: "transform 0.3s ease",
                          "&:hover": {
                            transform: "translateY(-5px) scale(1.02)",
                            boxShadow:
                              "15px 15px 45px #d9d9d9, -15px -15px 45px #ffffff",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            mb: 2,
                          }}
                        >
                          <Avatar
                            sx={{
                              bgcolor: "#ff4444",
                              width: 56,
                              height: 56,
                              boxShadow: "0 4px 15px rgba(255,68,68,0.3)",
                            }}
                          >
                            <CellTowerIcon sx={{ fontSize: 30 }} />
                          </Avatar>
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                              T{tower.id} - {tower.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: "#666" }}
                            >
                              {towerSensors.length} sensors installed
                            </Typography>
                          </Box>
                        </Box>

                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Paper
                              sx={{
                                p: 1.5,
                                bgcolor: "#f8f9fa",
                                borderRadius: "12px",
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{ color: "#666" }}
                              >
                                Latitude
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  fontFamily: "monospace",
                                }}
                              >
                                {tower.lat?.toFixed(4) || "N/A"}
                              </Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={6}>
                            <Paper
                              sx={{
                                p: 1.5,
                                bgcolor: "#f8f9fa",
                                borderRadius: "12px",
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{ color: "#666" }}
                              >
                                Longitude
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  fontFamily: "monospace",
                                }}
                              >
                                {tower.lng?.toFixed(4) || "N/A"}
                              </Typography>
                            </Paper>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Zoom>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}

        {/* Dashboard Modal */}
        <Dialog
          open={dashboardOpen}
          onClose={handleCloseDashboard}
          maxWidth="xl"
          fullWidth
          PaperProps={{
            sx: {
              height: "95vh",
              bgcolor: "#1e1e1e",
              borderRadius: "20px",
              overflow: "hidden",
            },
          }}
        >
          <DialogTitle
            sx={{
              bgcolor: "#2a2a2a",
              color: "white",
              borderBottom: "1px solid #333",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Weather Station Dashboard
            </Typography>
            <IconButton
              aria-label="close"
              onClick={handleCloseDashboard}
              sx={{
                color: "#888",
                "&:hover": {
                  color: "#ff4444",
                  backgroundColor: "rgba(255,68,68,0.1)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent
            sx={{ p: 0, bgcolor: "#1e1e1e", height: "calc(95vh - 0px)" }}
          >
            <iframe
              src={dashboardLink}
              title="Weather Dashboard"
              width="100%"
              height="98%"
              style={{
                border: "none",
                backgroundColor: "#1e1e1e",
              }}
              sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
            />
          </DialogContent>
        </Dialog>
      </Container>
    </Fade>
  );
}
